import { ApplicationStatus, LearnerStatus, LearningType, RegistrationStatus, AdminStatus } from "@tsin-core/entities";
import { Maybe } from "@tsin-shared/utils/types";
import { LearningFeeModel, LearningModel } from "./learning.model";
import { UserModel } from "./auth.model";
import { OrientationModel } from "./orientation.model";
import { SchoolsModel } from "./schools.model";


export interface BankDetailModel {
  institutionNumber: number | null;
  transitNumber: number | null;
  accountNumber: string | null;
  accountName: string | null;
  preferredEmail: string | null;
  applicationId: string | null;
  SIN: number | null;
  bursaryVoidCheque: string | ArrayBuffer | null;
  SINUpload: string | ArrayBuffer | null;
  bursaryAcknowledgement: boolean;
}

export interface ApplicationStateModel {
  applications: ApplicationModel[];
  selectApplication: ApplicationModel | undefined;
  isUserHasRegistrations: any;
  loading: LoadingApplicationState;
  error: any | null;
}

export interface ApplicationModel {
  _id: string;
  id: string;
  applicationNo: string;
  learning: LearningModel;
  foundationData: LearningModel;
  school: SchoolsModel;
  fee: LearningFeeModel;
  user: UserModel;
  orientation: OrientationModel;
  registrationFee: string;
  administrativeFee: string;
  learningId: string;
  schoolId: string;
  userId: string;
  feeId: string;
  foundationId: string;
  advancedId: string;
  status: ApplicationStatus; // DEPRECATED: Use registrationStatus instead
  registrationStatus: RegistrationStatus; // NEW: System-managed registration status
  adminStatus: AdminStatus | null; // NEW: Admin-managed learner lifecycle status
  photo?: string;
  adminAssigned?: string;
  paid: boolean;
  preferredName?: string;
  associatedMedicalSchool: string;
  postgraduateTrainingProgram: string;
  nacExamScore: number;
  mccqe1ExamScore?: number;
  nacExamAttempts?: number;
  mccqe1ExamAttempts?: number;
  highSchool?: string;
  highSchoolCountry?: string;
  highSchoolState?: string;
  residencyProgramLanguage?: string;
  PGYLevel: string;
  PEAPStartDate: string;
  PEAPEndDate: string;
  familyMedicine: boolean;
  orientationId: string;
  type: LearningType;
  comSimId: Maybe<string>;
  bankDetail?: BankDetailModel;
  // legacy bursary fields (kept for backward-compatibility with existing responses)
  imgBursaryAcknowledgement: Maybe<boolean>;
  imgBursarySocialInsuranceNumber: Maybe<string>;
  bursaryStatus: Maybe<string>;
  imgBursaryInstitutionNumber: Maybe<string>;
  imgBursaryTransitNumber: Maybe<string>;
  imgBursaryAccountNumber: Maybe<string>;
  imgBursaryVoidCheque: string | ArrayBuffer | null;
  imgBursarySinLetter?: string | ArrayBuffer | null;
  howToReceiveT4A?: string;
  t4AEmailAddress?: string;
  streetAddress?: string;
  unitNumber?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  province?: string;
}


export interface ApplicationSubmitRequest {
  learningId: Maybe<string>;
  schoolId: Maybe<string>;
  preferredName: Maybe<string>;
  associatedMedicalSchool: Maybe<string>;
  postgraduateTrainingProgram: Maybe<string>;
  PGYLevel: Maybe<string>;
  PEAPStartDate: Maybe<Date | string>;
  PEAPEndDate: Maybe<Date | string>;
  familyMedicine: boolean;
  orientationId: Maybe<string>;
  agreeToWatchOrientationRecording: boolean;
  comSimId: Maybe<string>;
  advancedId: Maybe<string>;
  foundationId: Maybe<string>;
  feeId: Maybe<string>;
  bankDetail: Partial<BankDetailModel> | undefined;
  type: LearningType;
  highSchool: Maybe<string>;
  nacExamAttempts: Maybe<number>;
  highSchoolCountry: Maybe<string>;
  highSchoolState: Maybe<string>;
  residencyProgramLanguage: Maybe<string>;
  mccqe1ExamAttempts: Maybe<number>;
  nacExamScore: Maybe<number>;
  mccqe1ExamScore?: Maybe<number>;
  howToReceiveT4A: Maybe<string>;
  t4AEmailAddress: Maybe<string>;
  streetAddress: Maybe<string>;
  unitNumber: Maybe<string>;
  city: Maybe<string>;
  province: Maybe<string>;
  postalCode: Maybe<string>;
  country: Maybe<string>;
}

export interface PrpFmApplicationSubmitRequest {
  // photo: File;
  comSimId: Maybe<string>;
  learningId: Maybe<string>;
  imgBursaryAcknowledgement: Maybe<boolean>;
  imgBursarySocialInsuranceNumber: Maybe<string>;
  imgBursaryInstitutionNumber: Maybe<string>;
  imgBursaryTransitNumber: Maybe<string>;
  imgBursaryAccountNumber: Maybe<string>;
  imgBursaryVoidCheque: Maybe<File>;
  associatedMedicalSchool: string;
  postgraduateTrainingProgram: string;
  PGYLevel: string;
  PEAPStartDate: string;
  PEAPEndDate: string;
  orientationId: string;
  familyMedicine: boolean;
  type: LearningType;
}


export interface UpdateBankDetailRequest {
  institutionNumber: number;
  transitNumber: number;
  accountNumber: string;
  accountName: string;
  preferredEmail: string;
  applicationId: string;
  SIN: number;
  bursaryVoidCheque: string;
  SINUpload: string;
  bursaryAcknowledgement: boolean;
  id: string;
}

export interface ApplicationStatusUpdateRequest { status: LearnerStatus | null; applicationIds: string[] | null; }

export interface ReassignLearnersRequest {
  foundationId: string,
  learningId: string,
  applicationIds: string[]
}

export interface LearningFeeSubmitRequest {
  registrationFee: string;
  administrativeFee: string;
  type: string;
  id?: string;
}

export interface FeeTypeListModel {
  id: string;
  type: LearningType;
  learningType: string;
  registrationFee: number;
  administrativeFee: number;
}

export enum LoadingApplicationState {
  loadingList = "loadApplicationList",
  loadingAddUpdate = "loadApplicationForm",
  loadingDelete = "loadApplicationDelete",
  notLoading = "notLoadingApplication",
}
